import React, { useCallback } from 'react';
import styles from './AddOrganizationModal.module.scss';
import { BaseModal } from 'components/UI';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import OrganizationForm from 'components/common/OrganizationForm/OrganizationForm';
import useOrganizationForm from 'hooks/useOrganizationForm';
import { useOrganizationsContext } from '../OrganizationsProvider';

export const AddOrganizationModal: React.FC = () => {
    const { accounts, isAddAccountModalOpen, handleCloseModal, handleSaveChildOrganizationAccount, editingAccount } =
        useOrganizationsContext();
    const {
        setShowExitPrompt,
        formData,
        errors,
        isSaving,
        handleInputChange,
        handlePhoneBlur,
        handleSave,
        parentOrganizations,
        businessTypeOptions,
        showExitPrompt,
        resetForm
    } = useOrganizationForm({
        isAddAccountModalOpen,
        handleCloseModal,
        editingAccount,
        accounts,
        handleSaveChildOrganizationAccount
    });

    // Memoize callback to prevent unnecessary re-renders
    const handleClose = useCallback(() => {
        setShowExitPrompt(true);
    }, [setShowExitPrompt]);

    // Memoize callback using resetForm from hook
    const resetFormAndClose = useCallback(() => {
        resetForm();
        handleCloseModal();
    }, [resetForm, handleCloseModal]);

    // Memoize callback for "Keep Editing" button
    const handleKeepEditing = useCallback(() => {
        setShowExitPrompt(false);
    }, [setShowExitPrompt]);

    // Memoize no-op function for when modal is saving
    const noOpClose = useCallback(() => {
        // Do nothing when saving to prevent modal close
    }, []);

    return (
        <>
            <BaseModal
                isOpen={isAddAccountModalOpen}
                onClose={isSaving ? noOpClose : handleClose} // Prevent closing while saving
                className={styles.addAccountModal}
            >
                <BaseModal.Header
                    showLogo={true}
                    showCloseButton={true}
                />

                <BaseModal.Content className={styles.modalContent}>
                    <div className={styles.formContainer}>
                        <OrganizationForm
                            isParentOrganization={false}
                            formData={formData}
                            parentOrganizations={parentOrganizations}
                            businessTypeOptions={businessTypeOptions}
                            errors={errors}
                            onInputChange={handleInputChange}
                            onPhoneBlur={handlePhoneBlur}
                            onSave={handleSave}
                            editingAccount={editingAccount}
                            isSaving={isSaving}
                        />
                    </div>
                </BaseModal.Content>
            </BaseModal>

            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={resetFormAndClose}
                    onKeepEditing={handleKeepEditing}
                />
            )}
        </>
    );
};

export default AddOrganizationModal;
